import React, { useEffect, useState } from 'react';

import { Flash } from '@primer/react';
import addDays from 'date-fns/addDays';
import startOfDay from 'date-fns/startOfDay';
import { useQuery } from '@apollo/client';

import { FETCH_PIPELINES_ASSOC_MODELS } from '../../utils/model_server';

import { AssayConfig } from './AssayConfig';
import Button from '../common/Button/Button';
import CardHeader from '../common/Labels/CardHeader';
import CardView from '../common/CardView/CardView';
import CreateAssayStepOne from './CreateAssayStepOne';
import PreviewHistorical from './PreviewHistorical';
import ScrollableBox from '../common/overlays/ScrollableBox';
import SelectBaselineChart from './SelectBaselineChart';
import { TimeRangeProvider } from '../contexts/TimeRangeContext/TimeRangeContext';

import {
  areEdgeValuesInappropriate,
  areEdgesRequiredAndIncomplete,
  areWeightsRequiredAndIncomplete,
} from '../../utils/assay_utils';
import createAssay from '../../utils/api_functions/assayCreation';
import useAssayCreationContext from './AssayCreationContext';
import { useMatchMutate } from '../../utils/hooks/use_match_mutate';
import usePipelineData from '../pipelines/PipelineDataContext';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

const CREATE_ASSAY_SCREENS = {
  SELECT_MODEL: 1,
  SELECT_BASELINE: 2,
  PREVIEW_HISTORICAL: 3,
}

const SelectBaselineFrameWrapper = ({
  newAssayConfig, setError, setNewAssayConfig, setNextIsDisabled,
}) => (
  <TimeRangeProvider usePollInterval={false} defaultTimeRange={{
    fromDate: newAssayConfig.baseline.Fixed.start_at ? new Date(newAssayConfig.baseline.Fixed.start_at) : startOfDay(new Date()),
    toDate: newAssayConfig.baseline.Fixed.end_at ? new Date(newAssayConfig.baseline.Fixed.end_at) : startOfDay(new Date()),
    type: 'custom'
  }}>
    <ScrollableBox>
      <SelectBaselineChart setError={setError} assayConfig={newAssayConfig} setAssayConfig={setNewAssayConfig} setNextIsDisabled={setNextIsDisabled} />
    </ScrollableBox>
  </TimeRangeProvider>
)

const PreviewAndFinishWrapper = ({
  newAssayConfig, setError, setNewAssayConfig,
}) => (
  <TimeRangeProvider usePollInterval={false} defaultTimeRange={{
    fromDate: new Date(newAssayConfig.baseline.Fixed.end_at),
    toDate: addDays(new Date(newAssayConfig.baseline.Fixed.end_at), 7),
    type: 'custom'
  }}>
    <ScrollableBox>
      <PreviewHistorical
        assayConfig={newAssayConfig}
        setAssayConfig={setNewAssayConfig}
        setError={setError}
      />
    </ScrollableBox>
  </TimeRangeProvider>
)

export const AssayConfirmCreationFrame = ({
  pipelineId,
  pipelineName,
  models,
  workspaceId,
  onClose,
  inferenceData
}) => {
  const [screen, setScreen] = useState(CREATE_ASSAY_SCREENS.SELECT_MODEL);
  const [newAssayConfig, setNewAssayConfig] = useState(new AssayConfig({ pipeline_name: pipelineName, pipeline_id: pipelineId }));
  const [nextIsDisabled, setNextIsDisabled] = useState(false);
  const [error, setError] = useState(null);
  const matchMutate = useMatchMutate();

  useEffect(() => {
    setNewAssayConfig(x => x.setPipeline(pipelineId, pipelineName).setWorkspace(workspaceId));
  }, [pipelineId, pipelineName, workspaceId])

  const handleBack = () => {
    setError(null);
    setNextIsDisabled(false);
    setScreen((screen) => {
      switch (screen) {
      case CREATE_ASSAY_SCREENS.SELECT_MODEL:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      case CREATE_ASSAY_SCREENS.SELECT_BASELINE:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      case CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL:
        return CREATE_ASSAY_SCREENS.SELECT_BASELINE;
      default:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      }
    })
  }

  const handleNext = () => {
    setScreen((screen) => {
      switch (screen) {
      case CREATE_ASSAY_SCREENS.SELECT_MODEL:
        return CREATE_ASSAY_SCREENS.SELECT_BASELINE;
      case CREATE_ASSAY_SCREENS.SELECT_BASELINE:
        return CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL;
      case CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      default:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      }
    })
  }

  const onBuild = async () => {
    try {
      if (areEdgesRequiredAndIncomplete(newAssayConfig)) {
        setError('Please provide all edge values if you wish to use a Custom Binning mode.')
      } else if (areWeightsRequiredAndIncomplete(newAssayConfig)) {
        setError('Please provide all weight values if you wish to use Custom Bin Weights.')
      } else if (areEdgeValuesInappropriate(newAssayConfig)) {
        setError('Please ensure your custom edge values are provided in sequential order.')
      } else {
        const ret = await createAssay(newAssayConfig);
        // assay_id shouldn't be 0, so this is fine.
        if (ret?.assay_id) {
          matchMutate(/assay-list/);
          // The SDK waits 30 seconds for assay data to be calculated.
          setTimeout(() => {
            matchMutate(/assay-results/)
          }, 10000);
          onClose();
        } else if (ret.msg.includes('assay_name_key')) {
          setError('An assay already exists with this name.')
        } else {
          setError('Failed to create assay.');
        }
      }
    } catch (e) {
      console.error(e);
      setError('Failed to create assay.');
    }
  }
  
  return (
    <CardView className="flex flex-col justify-evenly relative">
      {error && <Flash style={{
        position: 'absolute', top: 0, width: '80%', margin: '2px'
      }} variant="danger">{error}</Flash>}
      <div className="flex justify-between items-center mb-2">
        <CardHeader size="2xl">Create Assay</CardHeader>
        <Button
          variant="close"
          textColor="border-dark"
          hoverTextColor="border-darker"
          activeTextColor="info"
          onClick={onClose}
        />
      </div>
      <div className="my-2" style={{ height: '400px' }}>
        {
          screen === CREATE_ASSAY_SCREENS.SELECT_MODEL && <CreateAssayStepOne
            models={models}
            selectedModel={newAssayConfig.window.model}
            setSelected={x => setNewAssayConfig(conf => new AssayConfig(conf.setModel(x)))}
            applyMonitor={(x) => {
              setError(null);
              return setNewAssayConfig(conf => new AssayConfig(conf.applyMonitor(x)))
            }}
            setName={x => setNewAssayConfig(conf => new AssayConfig(conf, { name: x }))}
            name={newAssayConfig.name}
            setNextIsDisabled={setNextIsDisabled}
            inferenceData={inferenceData}
            assayConfig={newAssayConfig}
            setError={setError}
          />
        }
        {
          screen === CREATE_ASSAY_SCREENS.SELECT_BASELINE && (
            <SelectBaselineFrameWrapper 
              newAssayConfig={newAssayConfig}
              setError={setError}
              setNewAssayConfig={setNewAssayConfig}
              setNextIsDisabled={setNextIsDisabled}
            />
          )
        }
        {
          screen === CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL && (
            <PreviewAndFinishWrapper
              newAssayConfig={newAssayConfig}
              setNewAssayConfig={setNewAssayConfig}
              setError={setError}
            />
          )
        }
      </div>
      <div className="w-full flex justify-between my-2">
        <Button onClick={handleBack} size="large" style={{ visibility: screen === CREATE_ASSAY_SCREENS.SELECT_MODEL ? 'hidden' : 'visible' }}>Back</Button>
        <div className="flex justify-end">
          {
            screen !== CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL
              ? <Button
                variant="primary"
                size="large"
                onClick={handleNext}
                disabled={nextIsDisabled || error}>Next</Button>
              : <Button
                className="ml-2"
                variant="primary"
                size="large"
                // TODO: Fire the request up, close the modal  
                onClick={onBuild}
                disabled={nextIsDisabled || error}
              >
                Finish & Create Assay
              </Button>
          }
        </div>
      </div>
    </CardView>
  )
}

const ContextualAssayCreationFrame = ({
  handleBack, handleGoToAddSteps, onClose, cardTitle, inferenceData
}) => {
  const { pipeline } = usePipelineData();
  const assayProps = useAssayCreationContext();
  const pipelinesAssocModelsQueryRes = useQuery(FETCH_PIPELINES_ASSOC_MODELS, {
    skip: !pipeline?.pipelineID,
    variables: { pipeline_name: pipeline?.pipelineID },
    pollInterval: 1000
  });
  const { currentWorkspaceId } = useWorkspaceContext();
  
  return (
    <AssayConfirmCreationFrame
      models={pipelinesAssocModelsQueryRes.data?.model}
      pipelineId={pipeline.id}
      pipelineName={pipeline.pipelineID}
      workspaceId={currentWorkspaceId}
      setAssayPropertyByKey={assayProps.setAssayPropertyByKey}
      handleBack={handleBack}
      handleGoToAddSteps={handleGoToAddSteps}
      onClose={onClose}
      cardTitle={cardTitle}
      inferenceData={inferenceData}
    />
  )
}

export default ContextualAssayCreationFrame;
